import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaSearch,
  FaFilePdf,
  FaCalendarAlt,
  FaDownload,
  FaSpinner,
  FaGraduationCap,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "../components/common/Loader";
import { useFooter } from "../context/FooterContext";
import { buildApiUrl } from "../utils/apiConfig";

const MQPs = () => {
  const { setShowFooter } = useFooter();
  const [allMqps, setAllMqps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScheme, setSelectedScheme] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  useEffect(() => {
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    fetchAllMQPs();
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => setIsMobile(window.innerWidth <= 768);

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const fetchAllMQPs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/mqp?limit=500`);
      const mqps = response.data.mqps || [];
      setAllMqps(mqps);
    } catch (error) {
      console.error("Fetch MQPs error:", error);
      toast.error("Failed to fetch MQPs");
      setAllMqps([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (mqpId) => {
    if (!mqpId) {
      toast.error("Invalid paper ID");
      return;
    }
    try {
      window.open(buildApiUrl(`/api/mqp/view/${mqpId}`), "_blank");
      toast.success("Opening PDF...");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to open PDF");
    }
  };

  // Get unique schemes from MQPs
  const getUniqueSchemes = () => {
    const schemesSet = new Set();
    allMqps.forEach((mqp) => {
      if (mqp.scheme && mqp.status === "approved") {
        schemesSet.add(mqp.scheme);
      }
    });
    return Array.from(schemesSet).sort().reverse();
  };

  // Get filtered MQPs based on selected scheme and search
  const getFilteredMqps = () => {
    let filtered = allMqps.filter((mqp) => mqp.status === "approved");

    if (selectedScheme) {
      filtered = filtered.filter((mqp) => mqp.scheme === selectedScheme);
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (mqp) =>
          (mqp.title && mqp.title.toLowerCase().includes(searchLower)) ||
          (mqp.subjectCode &&
            mqp.subjectCode.toLowerCase().includes(searchLower)) ||
          (mqp.subject && mqp.subject.toLowerCase().includes(searchLower)) ||
          (mqp.examYear && mqp.examYear.includes(searchTerm)),
      );
    }

    return filtered;
  };

  const uniqueSchemes = getUniqueSchemes();
  const filteredMqps = getFilteredMqps();
  const totalPapers = filteredMqps.length;

  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "white";

  if (loading) return <Loader />;

  // Scheme Selection Screen
  if (!selectedScheme) {
    return (
      <div
        style={{
          maxWidth: "600px",
          margin: "0 auto",
          padding: isMobile ? "1rem" : "2rem",
          background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div
            style={{
              background: "linear-gradient(135deg, #ff6a00, #ff8c3a)",
              width: "70px",
              height: "70px",
              borderRadius: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 1rem auto",
            }}
          >
            <FaFilePdf size={32} color="white" />
          </div>
          <h1
            style={{
              fontSize: isMobile ? "1.8rem" : "2rem",
              fontWeight: "bold",
              color: getTextColor(),
              marginBottom: "0.5rem",
            }}
          >
            Model Question Papers
          </h1>
          <p style={{ color: getTextSecondary() }}>
            Select your scheme to view model question papers
          </p>
        </div>

        {uniqueSchemes.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "3rem",
              background: getCardBackground(),
              borderRadius: "16px",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <FaFilePdf size={48} color={getTextSecondary()} />
            <p style={{ color: getTextSecondary(), marginTop: "1rem" }}>
              No schemes available. Please upload papers first.
            </p>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
              gap: "1rem",
            }}
          >
            {uniqueSchemes.map((scheme) => {
              const paperCount = allMqps.filter(
                (mqp) => mqp.scheme === scheme && mqp.status === "approved",
              ).length;
              return (
                <motion.div
                  key={scheme}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ y: -4, scale: 1.02 }}
                  onClick={() => setSelectedScheme(scheme)}
                  style={{
                    background: getCardBackground(),
                    borderRadius: "16px",
                    padding: "1.5rem",
                    border: `1px solid ${getBorderColor()}`,
                    cursor: "pointer",
                    textAlign: "center",
                    transition: "all 0.3s",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  }}
                >
                  <div
                    style={{
                      width: "50px",
                      height: "50px",
                      background: "linear-gradient(135deg, #ff6a00, #ff8c3a)",
                      borderRadius: "25px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 0.8rem auto",
                    }}
                  >
                    <FaGraduationCap size={24} color="white" />
                  </div>
                  <h2
                    style={{
                      color: getTextColor(),
                      margin: 0,
                      fontSize: "1.2rem",
                      fontWeight: "bold",
                    }}
                  >
                    {scheme} Scheme
                  </h2>
                  <p
                    style={{
                      color: "#ff6a00",
                      margin: "0.5rem 0 0",
                      fontSize: "0.7rem",
                    }}
                  >
                    {paperCount} paper{paperCount !== 1 ? "s" : ""}
                  </p>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Papers Display Screen
  return (
    <div
      style={{
        maxWidth: "1000px",
        margin: "0 auto",
        padding: isMobile ? "1rem" : "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        minHeight: "calc(100vh - 200px)",
      }}
    >
      <button
        onClick={() => setSelectedScheme("")}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.5rem",
          color: "#ff6a00",
          background: "none",
          border: "none",
          cursor: "pointer",
          marginBottom: "1rem",
          fontSize: "0.9rem",
        }}
      >
        ← Back to Schemes
      </button>

      <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
        <h1
          style={{
            fontSize: isMobile ? "1.5rem" : "1.8rem",
            fontWeight: "bold",
            color: getTextColor(),
          }}
        >
          {selectedScheme} Scheme - Question Papers
        </h1>
        <p
          style={{
            color: getTextSecondary(),
            fontSize: "0.8rem",
            marginTop: "0.3rem",
          }}
        >
          {totalPapers} papers available
        </p>
      </div>

      {/* Search Bar */}
      <div
        style={{
          background: getCardBackground(),
          borderRadius: "12px",
          padding: "0.5rem",
          marginBottom: "1.5rem",
          border: `1px solid ${getBorderColor()}`,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            alignItems: "center",
          }}
        >
          <FaSearch
            color={getTextSecondary()}
            style={{ marginLeft: "0.8rem" }}
          />
          <input
            type="text"
            placeholder="Search by title, subject code, or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              padding: "0.7rem 0.5rem",
              border: "none",
              outline: "none",
              background: "transparent",
              color: getTextColor(),
            }}
          />
        </div>
      </div>

      {/* Papers List */}
      {totalPapers === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            background: getCardBackground(),
            borderRadius: "16px",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <FaFilePdf size={48} color={getTextSecondary()} />
          <h3 style={{ color: getTextColor(), marginTop: "1rem" }}>
            No papers found
          </h3>
          <p style={{ color: getTextSecondary() }}>Try adjusting your search</p>
        </div>
      ) : (
        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.8rem" }}
        >
          {filteredMqps.map((paper) => (
            <motion.div
              key={paper._id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              whileHover={{ x: 5 }}
              style={{
                background: getCardBackground(),
                borderRadius: "12px",
                padding: "0.8rem 1rem",
                border: `1px solid ${getBorderColor()}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.8rem",
                cursor: "pointer",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
              onClick={() => handleDownload(paper._id)}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.8rem",
                    flexWrap: "wrap",
                    marginBottom: "0.3rem",
                  }}
                >
                  <FaFilePdf color="#ef4444" size={20} />
                  {paper.subjectCode && (
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontSize: "0.7rem",
                        color: "#ff6a00",
                        background: "rgba(255,106,0,0.1)",
                        padding: "0.2rem 0.5rem",
                        borderRadius: "4px",
                      }}
                    >
                      {paper.subjectCode}
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: "0.7rem",
                      color: getTextSecondary(),
                      display: "flex",
                      alignItems: "center",
                      gap: "0.2rem",
                    }}
                  >
                    <FaCalendarAlt size={10} /> {paper.examYear}
                  </span>
                  {paper.examType && paper.examType !== "Model" && (
                    <span
                      style={{
                        fontSize: "0.6rem",
                        color: getTextSecondary(),
                        background: isDarkMode ? "#2b2d31" : "#f0f0f0",
                        padding: "0.15rem 0.4rem",
                        borderRadius: "4px",
                      }}
                    >
                      {paper.examType}
                    </span>
                  )}
                </div>
                <p
                  style={{
                    color: getTextColor(),
                    fontSize: "0.85rem",
                    fontWeight: "500",
                    margin: 0,
                  }}
                >
                  {paper.title || (paper.subject ? paper.subject : "Untitled")}
                </p>
                {paper.subject && paper.subject !== paper.title && (
                  <p
                    style={{
                      color: getTextSecondary(),
                      fontSize: "0.7rem",
                      marginTop: "0.2rem",
                      margin: 0,
                    }}
                  >
                    {paper.subject}
                  </p>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(paper._id);
                }}
                style={{
                  padding: "0.4rem 1rem",
                  background: "#ff6a00",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.3rem",
                  fontSize: "0.75rem",
                  fontWeight: "500",
                  transition: "transform 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "scale(1.05)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
              >
                <FaDownload size={12} /> Download
              </button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MQPs;

